/**
 * PagePubliqueSettings — onglet "Page publique" dans Paramètres.
 * - Si l'add-on n'est pas actif (HTTP 402) : upsell.
 * - Sinon : formulaire de configuration + bouton publier + lien partageable.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import { pagePubliqueApi } from "../services/api";
import useToast from "../hooks/useToast";
import Card from "./Card";
import Input from "./Input";
import Button from "./Button";
import Badge from "./Badge";

const SITE_BASE = "https://mon-ministere.com";

export default function PagePubliqueSettings() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [addonLocked, setAddonLocked] = useState(false);
  const [page, setPage] = useState(null);
  const [form, setForm] = useState({
    titre: "",
    description: "",
    adresse: "",
    telephone: "",
    email_contact: "",
    couleur_principale: "#E4A72C",
    horaires_culte: [],
    show_events: true,
    show_don: true,
    show_contact: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pagePubliqueApi.get()
      .then(({ data }) => {
        if (cancelled) return;
        setPage(data || null);
        if (data) {
          setForm({
            titre: data.titre || "",
            description: data.description || "",
            adresse: data.adresse || "",
            telephone: data.telephone || "",
            email_contact: data.email_contact || "",
            couleur_principale: data.couleur_principale || "#E4A72C",
            horaires_culte: Array.isArray(data.horaires_culte) ? data.horaires_culte : [],
            show_events: data.show_events,
            show_don: data.show_don,
            show_contact: data.show_contact,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 402) {
          setAddonLocked(true);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await pagePubliqueApi.configurer(form);
      setPage(data);
      toast.success("Configuration enregistrée");
    } catch (err) {
      if (err.response?.status === 402) setAddonLocked(true);
      else toast.error(err.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const togglePublier = async () => {
    if (!page) return;
    try {
      const { data } = await pagePubliqueApi.publier(!page.is_active);
      setPage(data);
      toast.success(data.is_active ? "Page publiée" : "Page mise hors ligne");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // ─── Streaming ───
  const [streamForm, setStreamForm] = useState({
    stream_actif: false,
    stream_url: "",
    stream_type: "youtube",
    stream_titre: "",
    prochain_stream_date: "",
  });
  const [savingStream, setSavingStream] = useState(false);
  const [replayModalOpen, setReplayModalOpen] = useState(false);

  // Sync streamForm chaque fois que la page change (chargement initial + sauvegardes)
  useEffect(() => {
    if (!page) return;
    setStreamForm({
      stream_actif: !!page.stream_actif,
      stream_url: page.stream_url || "",
      stream_type: page.stream_type || "youtube",
      stream_titre: page.stream_titre || "",
      prochain_stream_date: page.prochain_stream_date
        ? new Date(page.prochain_stream_date).toISOString().slice(0, 16)
        : "",
    });
  }, [page]);

  const saveStream = async (overrides = {}) => {
    setSavingStream(true);
    try {
      const payload = {
        stream_actif: streamForm.stream_actif,
        stream_url: streamForm.stream_url || null,
        stream_type: streamForm.stream_type || null,
        stream_titre: streamForm.stream_titre || null,
        prochain_stream_date: streamForm.prochain_stream_date
          ? new Date(streamForm.prochain_stream_date).toISOString()
          : null,
        replays: Array.isArray(page?.replays) ? page.replays : [],
        ...overrides,
      };
      const { data } = await pagePubliqueApi.stream(payload);
      setPage(data);
      toast.success("Streaming sauvegardé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingStream(false);
    }
  };

  const addReplay = async (replay) => {
    const next = [...((page && page.replays) || []), replay].slice(0, 10);
    await saveStream({ replays: next });
    setReplayModalOpen(false);
  };

  const removeReplay = async (index) => {
    const next = ((page && page.replays) || []).filter((_, i) => i !== index);
    await saveStream({ replays: next });
  };

  const addHoraire = () => {
    setForm((f) => ({
      ...f,
      horaires_culte: [...f.horaires_culte, { jour: "Dimanche", heure: "10h00", type: "Culte" }],
    }));
  };

  const updateHoraire = (i, field, value) => {
    setForm((f) => ({
      ...f,
      horaires_culte: f.horaires_culte.map((h, idx) => idx === i ? { ...h, [field]: value } : h),
    }));
  };

  const removeHoraire = (i) => {
    setForm((f) => ({
      ...f,
      horaires_culte: f.horaires_culte.filter((_, idx) => idx !== i),
    }));
  };

  if (loading) {
    return <Card><div style={{ color: colors.textMuted }}>Chargement…</div></Card>;
  }

  if (addonLocked) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "16px 8px" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🌐</div>
          <h2 style={{ fontFamily: fonts.heading, fontSize: 24, marginBottom: 8 }}>
            Page publique de votre église
          </h2>
          <p style={{ color: colors.textMuted, fontSize: 14, maxWidth: 460, margin: "0 auto 16px", lineHeight: 1.6 }}>
            Donnez une vitrine à votre église. Partageable sur WhatsApp · Visible sur Google · Dons et contact en ligne.
          </p>
          <div style={{ display: "inline-block", padding: "8px 18px", background: colors.goldDim, border: `1px solid ${colors.goldBorder}`, borderRadius: 999, color: colors.gold, fontWeight: 600, fontSize: 14, marginBottom: 16 }}>
            +7 €/mois · Inclus dans Pro et Enterprise
          </div>
          <div>
            <Button>Activer ce module →</Button>
          </div>
        </div>
      </Card>
    );
  }

  const slug = page?.slug;
  const publicUrl = slug ? `${SITE_BASE}/eglise/${slug}` : null;
  const shareWA = slug
    ? `https://wa.me/?text=${encodeURIComponent(`Retrouvez notre église sur Mon Ministère : ${publicUrl}`)}`
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Section 1 — Informations */}
      <Card>
        <SectionTitle>1. Informations</SectionTitle>
        <Grid>
          <Input label="Titre de la page" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} />
          <Input label="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
          <Input label="Email de contact" value={form.email_contact} onChange={(e) => setForm({ ...form, email_contact: e.target.value })} />
          <ColorPicker label="Couleur principale" value={form.couleur_principale} onChange={(v) => setForm({ ...form, couleur_principale: v })} />
        </Grid>
        <div style={{ marginTop: 12 }}>
          <Input label="Adresse complète" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Église évangélique au cœur de…"
            style={textareaStyle}
          />
        </div>
      </Card>

      {/* Section 2 — Horaires */}
      <Card>
        <SectionTitle>2. Horaires des cultes</SectionTitle>
        {form.horaires_culte.length === 0 && (
          <p style={{ color: colors.textMuted, fontSize: 13, marginBottom: 12 }}>
            Aucun horaire configuré.
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {form.horaires_culte.map((h, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "center" }}>
              <input value={h.jour}  onChange={(e) => updateHoraire(i, "jour", e.target.value)}  placeholder="Jour"  style={miniInput} />
              <input value={h.heure} onChange={(e) => updateHoraire(i, "heure", e.target.value)} placeholder="Heure" style={miniInput} />
              <input value={h.type || ""} onChange={(e) => updateHoraire(i, "type", e.target.value)} placeholder="Type (Culte, Prière…)" style={miniInput} />
              <button onClick={() => removeHoraire(i)} style={{ padding: "8px 10px", color: colors.rose, fontSize: 18 }}>×</button>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <Button variant="ghost" size="sm" onClick={addHoraire}>+ Ajouter un horaire</Button>
        </div>
      </Card>

      {/* Section 3 — Options */}
      <Card>
        <SectionTitle>3. Options</SectionTitle>
        <Check label="Afficher les événements"            value={form.show_events}  onChange={(v) => setForm({ ...form, show_events: v })} />
        <Check label="Activer les dons en ligne"          value={form.show_don}     onChange={(v) => setForm({ ...form, show_don: v })} />
        <Check label="Afficher le formulaire de contact"  value={form.show_contact} onChange={(v) => setForm({ ...form, show_contact: v })} />
      </Card>

      {/* Bouton enregistrer */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={save} disabled={saving}>
          {saving ? "Enregistrement…" : (page ? "Enregistrer les modifications" : "Créer la page publique")}
        </Button>
      </div>

      {/* Section Streaming — visible une fois la page créée */}
      {page && (
        <Card>
          <SectionTitle>📺 Culte en direct</SectionTitle>

          <label style={labelStyle}>Statut du live</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            <Radio
              label="Pas de live en ce moment"
              checked={!streamForm.stream_actif}
              onChange={() => setStreamForm({ ...streamForm, stream_actif: false })}
            />
            <Radio
              label="🔴 Live en cours maintenant"
              checked={streamForm.stream_actif}
              onChange={() => setStreamForm({ ...streamForm, stream_actif: true })}
              activeColor="#E05B6A"
            />
          </div>

          <Input
            label="URL du live"
            value={streamForm.stream_url}
            onChange={(e) => setStreamForm({ ...streamForm, stream_url: e.target.value })}
            placeholder="https://youtube.com/live/xxx ou https://facebook.com/…"
          />
          <p style={{ fontSize: 12, color: colors.textMuted, margin: "4px 0 12px" }}>
            Accepte : YouTube, Facebook, ou tout autre lien iframe-able.
          </p>

          <Input
            label="Titre du live"
            value={streamForm.stream_titre}
            onChange={(e) => setStreamForm({ ...streamForm, stream_titre: e.target.value })}
            placeholder="Ex: Culte du dimanche — Louange & Parole"
          />

          {!streamForm.stream_actif && (
            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Prochain live (date et heure)</label>
              <input
                type="datetime-local"
                value={streamForm.prochain_stream_date}
                onChange={(e) => setStreamForm({ ...streamForm, prochain_stream_date: e.target.value })}
                style={miniInput}
              />
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <Button onClick={() => saveStream()} disabled={savingStream}>
              {savingStream ? "Enregistrement…" : "💾 Sauvegarder le streaming"}
            </Button>
          </div>

          {/* Replays */}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${colors.borderLight}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h4 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
                Replays ({(page.replays || []).length}/10)
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReplayModalOpen(true)}
                disabled={(page.replays || []).length >= 10}
              >
                + Ajouter
              </Button>
            </div>
            {(page.replays || []).length === 0 ? (
              <p style={{ color: colors.textMuted, fontSize: 13 }}>Aucun replay pour l'instant.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {(page.replays || []).map((r, i) => (
                  <li key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                    padding: "10px 12px", background: colors.bg, borderRadius: 8, border: `1px solid ${colors.border}`,
                  }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        ▶ {r.titre}
                      </div>
                      {r.date && <div style={{ fontSize: 12, color: colors.textMuted }}>{r.date}</div>}
                    </div>
                    <button
                      onClick={() => removeReplay(i)}
                      title="Supprimer"
                      style={{ color: colors.rose, fontSize: 18, padding: "4px 8px" }}
                    >
                      🗑
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      )}

      {replayModalOpen && (
        <ReplayModal
          onClose={() => setReplayModalOpen(false)}
          onAdd={addReplay}
        />
      )}

      {/* Section 4 — Lien & partage (visible une fois la page créée) */}
      {page && publicUrl && (
        <Card>
          <SectionTitle>4. Votre lien</SectionTitle>
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>🔗 URL publique</div>
            <div style={{ color: colors.gold, fontFamily: "monospace", fontSize: 14, wordBreak: "break-all" }}>{publicUrl}</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Lien copié"); }}>
              📋 Copier
            </Button>
            <Link to={`/eglise/${slug}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <Button variant="ghost" size="sm">👁 Aperçu</Button>
            </Link>
            <a href={shareWA} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <Button variant="ghost" size="sm">📤 Partager sur WhatsApp</Button>
            </a>
            <Button variant={page.is_active ? "danger" : "primary"} size="sm" onClick={togglePublier}>
              {page.is_active ? "Mettre hors ligne" : "Publier"}
            </Button>
          </div>
          <div style={{ marginTop: 12 }}>
            {page.is_active ? (
              <Badge variant="success" dot>● Page active · Visible sur Google</Badge>
            ) : (
              <Badge variant="warning" dot>○ Page hors ligne</Badge>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Helpers ───

function SectionTitle({ children }) {
  return (
    <h3 style={{ fontFamily: fonts.heading, fontSize: 20, fontWeight: 700, marginBottom: 16 }}>
      {children}
    </h3>
  );
}

function Grid({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
      {children}
    </div>
  );
}

function Radio({ label, checked, onChange, activeColor }) {
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px",
      background: checked ? colors.goldDim : "transparent",
      border: `1px solid ${checked ? (activeColor || colors.gold) : colors.border}`,
      borderRadius: 10,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: checked ? 600 : 400,
      color: checked ? (activeColor || colors.gold) : colors.text,
    }}>
      <input type="radio" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

function ReplayModal({ onClose, onAdd }) {
  const [titre, setTitre] = useState("");
  const [url, setUrl] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!titre || !url) return;
    setSubmitting(true);
    try {
      await onAdd({ titre, url, date });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 500, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        width: "100%", maxWidth: 480,
        padding: 24,
      }}>
        <h3 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 16 }}>Ajouter un replay</h3>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input label="Titre" value={titre} onChange={(e) => setTitre(e.target.value)} required />
          <Input label="URL YouTube/Facebook" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=…" required />
          <div>
            <label style={labelStyle}>Date du culte</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required style={miniInput} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Ajout…" : "Ajouter"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Check({ label, value, onChange }) {
  return (
    <label style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 0", borderBottom: `1px solid ${colors.borderLight}`,
      cursor: "pointer",
    }}>
      <span style={{ fontSize: 14 }}>{label}</span>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

function ColorPicker({ label, value, onChange }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 48, height: 38, border: "none", background: "transparent", cursor: "pointer" }} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} style={miniInput} />
      </div>
    </label>
  );
}

const labelStyle = { fontSize: 13, color: colors.textMuted, fontWeight: 500, marginBottom: 4, display: "block" };

const miniInput = {
  background: colors.bg,
  color: colors.text,
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 13,
  outline: "none",
  width: "100%",
};

const textareaStyle = {
  ...miniInput,
  width: "100%",
  resize: "vertical",
  fontFamily: fonts.body,
  fontSize: 14,
  padding: "12px 14px",
};
